import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Paper,
  Typography,
} from '@mui/material';
import PropTypes from 'prop-types';
import { formatTableHeader } from './tableHeader';

function defaultCopyValue(row, col) {
  if (typeof col.getCopyValue === 'function') {
    return col.getCopyValue(row);
  }
  if (col.copy === false) return null;
  const raw = row[col.id];
  if (raw == null) return '—';
  if (typeof raw === 'object') {
    if (raw.label) return String(raw.label);
    return null;
  }
  if (typeof raw === 'boolean') return raw ? 'Done' : '—';
  const text = String(raw).trim();
  return text || '—';
}

export function ParticipantTable({
  columns,
  rows,
  loading = false,
  sortBy = null,
  sortDir = 'asc',
  onSort = null,
  onCopyCell = null,
  emptyMessage = 'No participants',
  getRowId = (row) => row.id,
  className = '',
  emptyCellClassName = '',
}) {
  const handleCellClick = (event, row, col) => {
    if (!onCopyCell) return;
    if (event.target.closest('a, button, input, textarea, select, [data-no-copy]')) {
      return;
    }
    const fromAttr = event.target.closest('[data-copy-value]');
    const value = fromAttr?.getAttribute('data-copy-value') ?? defaultCopyValue(row, col);
    if (value == null) return;
    onCopyCell(value, event.clientX, event.clientY);
  };

  let body;
  if (loading) {
    body = (
      <TableRow>
        <TableCell
          colSpan={Math.max(columns.length, 1)}
          className="onyx-participant-table__loading-cell"
        >
          <Box
            className="onyx-participant-table__loading"
            data-testid="onyx-participant-table-loading"
            role="status"
            aria-live="polite"
            aria-busy="true"
          >
            <CircularProgress size={28} />
            <Typography variant="uiBody">Loading members…</Typography>
          </Box>
        </TableCell>
      </TableRow>
    );
  } else if (rows.length === 0) {
    body = (
      <TableRow>
        <TableCell
          colSpan={columns.length}
          className={emptyCellClassName || undefined}
        >
          <Typography variant="uiBody">{emptyMessage}</Typography>
        </TableCell>
      </TableRow>
    );
  } else {
    body = rows.map((row) => (
      <TableRow key={getRowId(row)} hover>
        {columns.map((col) => {
          const copyable = onCopyCell && col.copy !== false;
          const cellClass = [
            copyable ? 'onyx-cell-copyable' : '',
            typeof col.cellClassName === 'function'
              ? col.cellClassName(row)
              : (col.cellClassName || ''),
          ].filter(Boolean).join(' ');
          return (
            <TableCell
              key={col.id}
              align={col.align || 'left'}
              className={cellClass || undefined}
              onClick={(event) => handleCellClick(event, row, col)}
            >
              {col.render ? col.render(row) : row[col.id]}
            </TableCell>
          );
        })}
      </TableRow>
    ));
  }

  return (
    <TableContainer
      component={Paper}
      elevation={0}
      className={['onyx-participant-table', className].filter(Boolean).join(' ')}
      data-testid="onyx-participant-table"
    >
      <Table data-variant="lighthouse" size="small">
        <TableHead>
          <TableRow>
            {columns.map((col) => {
              const sortable = col.sortable !== false && typeof onSort === 'function'
                && col.id !== 'invite'
                && col.id !== 'pluto'
                && col.id !== 'sign_icf'
                && col.id !== 'review'
                && col.id !== 'notes'
                && col.id !== 'determine';
              return (
                <TableCell
                  key={col.id}
                  align={col.align || 'left'}
                  className={col.headerClassName}
                >
                  {sortable ? (
                    <TableSortLabel
                      active={sortBy === col.id}
                      direction={sortBy === col.id ? sortDir : 'asc'}
                      onClick={() => onSort(col.id)}
                    >
                      {formatTableHeader(col.label)}
                    </TableSortLabel>
                  ) : (
                    formatTableHeader(col.label)
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        </TableHead>
        <TableBody>{body}</TableBody>
      </Table>
    </TableContainer>
  );
}

ParticipantTable.propTypes = {
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      sortable: PropTypes.bool,
      copy: PropTypes.bool,
      getCopyValue: PropTypes.func,
      align: PropTypes.string,
      render: PropTypes.func,
      cellClassName: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
      headerClassName: PropTypes.string,
    })
  ).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  sortBy: PropTypes.string,
  sortDir: PropTypes.oneOf(['asc', 'desc']),
  onSort: PropTypes.func,
  onCopyCell: PropTypes.func,
  emptyMessage: PropTypes.string,
  getRowId: PropTypes.func,
  className: PropTypes.string,
  emptyCellClassName: PropTypes.string,
};
